import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AuditLog from "@/components/AuditLog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Users, FileText, AlertCircle, LogOut, BarChart3, X, Loader2, Lock, DollarSign, TrendingUp, Repeat, AlertTriangle, MapPin, Send, Target, Inbox, Map, Receipt, Trash2, Mail, MessageCircle, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

export default function SuperAdmin() {
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [subStats, setSubStats] = useState(null);
  const [subRange, setSubRange] = useState(() => {
    const t = new Date();
    return {
      from: new Date(t.getFullYear(), t.getMonth(), 1).toISOString().slice(0, 10),
      to: t.toISOString().slice(0, 10),
    };
  });
  const [statsFor, setStatsFor] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const load = () => {
    Promise.all([api.get("/admin/stats"), api.get("/admin/companies")]).then(([s, c]) => {
      setStats(s.data); setCompanies(c.data);
    });
  };

  const loadSubs = () => {
    api.get("/admin/subscription-stats", { params: { from_date: subRange.from, to_date: subRange.to } })
      .then((r) => setSubStats(r.data))
      .catch(() => {});
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { loadSubs(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [subRange.from, subRange.to]);

  const setSubPreset = (preset) => {
    const now = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (preset === "month") setSubRange({ from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(now) });
    else if (preset === "30d") { const d = new Date(now); d.setDate(d.getDate() - 30); setSubRange({ from: iso(d), to: iso(now) }); }
    else if (preset === "ytd") setSubRange({ from: iso(new Date(now.getFullYear(), 0, 1)), to: iso(now) });
    else if (preset === "all") setSubRange({ from: "2020-01-01", to: iso(now) });
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/companies/${id}`, { status });
      toast.success(`Company ${status}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-slate-900 flex items-center justify-center text-white font-bold">A</div>
            <div>
              <div className="font-display font-bold text-slate-900">WindowScreenQuote Admin</div>
              <div className="text-xs text-slate-500">Platform owner console</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 hidden sm:inline">{user?.email}</span>
            <Button data-testid="admin-coverage-btn" size="sm" variant="outline" onClick={() => nav("/admin/coverage")} className="rounded-xl">
              <Map className="w-4 h-4 mr-1.5" /> Coverage
            </Button>
            <Button data-testid="admin-transactions-btn" size="sm" variant="outline" onClick={() => nav("/admin/transactions")} className="rounded-xl">
              <Receipt className="w-4 h-4 mr-1.5" /> Billing
            </Button>
            <Button data-testid="admin-subscriptions-btn" size="sm" variant="outline" onClick={() => nav("/admin/subscriptions")} className="rounded-xl">
              <CreditCard className="w-4 h-4 mr-1.5" /> Subscriptions
            </Button>
            <Button data-testid="admin-outreach-btn" size="sm" variant="outline" onClick={() => nav("/admin/outreach")} className="rounded-xl">
              <Send className="w-4 h-4 mr-1.5" /> Outreach
            </Button>
            <Button data-testid="admin-conversion-btn" size="sm" variant="outline" onClick={() => nav("/admin/conversion")} className="rounded-xl">
              <TrendingUp className="w-4 h-4 mr-1.5" /> Conversion
            </Button>
            <Button data-testid="admin-economics-btn" size="sm" variant="outline" onClick={() => nav("/admin/economics")} className="rounded-xl">
              <DollarSign className="w-4 h-4 mr-1.5" /> Economics
            </Button>
            <Button data-testid="admin-sources-btn" size="sm" variant="outline" onClick={() => nav("/admin/sources")} className="rounded-xl">
              <TrendingUp className="w-4 h-4 mr-1.5" /> Sources
            </Button>
            <Button data-testid="admin-email-btn" size="sm" variant="outline" onClick={() => nav("/admin/email")} className="rounded-xl">
              <Mail className="w-4 h-4 mr-1.5" /> Emails
            </Button>
            <Button data-testid="admin-support-btn" size="sm" variant="outline" onClick={() => nav("/admin/support")} className="rounded-xl">
              <MessageCircle className="w-4 h-4 mr-1.5" /> Support
            </Button>
            <Button data-testid="admin-lead-hub-btn" size="sm" onClick={() => nav("/admin/leads")} className="rounded-xl bg-[#C75D33] hover:bg-[#A84C28] text-white">
              <Inbox className="w-4 h-4 mr-1.5" /> Lead Hub
            </Button>
            <Button data-testid="admin-logout-btn" variant="outline" size="sm" onClick={async () => { await logout(); nav("/login"); }} className="rounded-xl">
              <LogOut className="w-4 h-4 mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <h1 className="font-display text-3xl font-bold text-slate-900 mb-7" data-testid="admin-title">Platform overview</h1>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Building2} label="Companies" value={stats?.total_companies ?? 0} testid="stat-companies" />
          <StatCard icon={Users} label="Active" value={stats?.active_companies ?? 0} accent="text-emerald-600" testid="stat-active" />
          <StatCard icon={AlertCircle} label="Suspended" value={stats?.suspended_companies ?? 0} accent="text-amber-600" testid="stat-suspended" />
          <StatCard icon={FileText} label="Total quotes" value={stats?.total_leads ?? 0} accent="text-[#C75D33]" testid="stat-leads" />
        </div>

        <SubscriptionPanel data={subStats} range={subRange} setRange={setSubRange} setPreset={setSubPreset} />

        <LeadSourcesPanel />

        <DisputesPanel />

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="font-display text-lg font-bold text-slate-900">Companies</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-6 py-3 font-bold">Company</th>
                  <th className="text-left px-6 py-3 font-bold">Slug</th>
                  <th className="text-left px-6 py-3 font-bold">Quotes</th>
                  <th className="text-left px-6 py-3 font-bold">Created</th>
                  <th className="text-left px-6 py-3 font-bold">Status</th>
                  <th className="text-left px-6 py-3 font-bold w-0"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {companies.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50" data-testid={`admin-row-${c.id}`}>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-slate-900">{c.business_name}</div>
                      <div className="text-xs text-slate-500">{c.owner_email}</div>
                    </td>
                    <td className="px-6 py-4 font-mono text-xs">/{c.slug}/quote</td>
                    <td className="px-6 py-4 font-bold">{c.lead_count}</td>
                    <td className="px-6 py-4 text-slate-500">{new Date(c.created_at).toLocaleDateString()}</td>
                    <td className="px-6 py-4">
                      <Select value={c.status} onValueChange={(v) => setStatus(c.id, v)}>
                        <SelectTrigger className="h-9 w-36 rounded-lg" data-testid={`admin-status-${c.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="suspended">Suspended</SelectItem>
                          <SelectItem value="disabled">Disabled</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-lg h-9"
                          onClick={() => setStatsFor(c)}
                          data-testid={`admin-stats-btn-${c.id}`}
                        >
                          <BarChart3 className="w-4 h-4 mr-1.5" /> Stats
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-lg h-9 text-rose-600 border-rose-200 hover:bg-rose-50"
                          onClick={() => setDeleteFor(c)}
                          data-testid={`admin-delete-company-btn-${c.id}`}
                          title="Delete company + all operational data"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {companies.length === 0 && (
                  <tr><td colSpan={6} className="px-6 py-10 text-center text-slate-500">No companies yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {statsFor && <CompanyStatsModal company={statsFor} onClose={() => setStatsFor(null)} />}
      {deleteFor && <DeleteCompanyModal company={deleteFor} onClose={() => setDeleteFor(null)} onDeleted={() => { setDeleteFor(null); load(); }} />}
    </div>
  );
}

/* ----- Platform subscription overview ----- */

function TrendChart({ trend }) {
  // Lightweight inline-SVG line chart — quotes per day
  const W = 600;
  const H = 140;
  const PAD_X = 8;
  const PAD_Y = 10;
  const counts = trend.map((t) => t.count);
  const maxCount = Math.max(1, ...counts);
  const stepX = (W - PAD_X * 2) / Math.max(1, trend.length - 1);

  const pts = trend.map((t, i) => {
    const x = PAD_X + i * stepX;
    const y = PAD_Y + (H - PAD_Y * 2) * (1 - t.count / maxCount);
    return { x, y, ...t };
  });

  const linePath = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(" ");
  const areaPath = `${linePath} L ${pts[pts.length - 1].x.toFixed(2)} ${H - PAD_Y} L ${pts[0].x.toFixed(2)} ${H - PAD_Y} Z`;

  const startLabel = trend[0]?.date;
  const endLabel = trend[trend.length - 1]?.date;

  return (
    <div className="rounded-2xl border border-slate-200 overflow-hidden bg-white" data-testid="trend-chart">
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-600">Quotes per day</div>
        <div className="text-[10px] text-slate-500 tabular-nums">peak {maxCount}/day</div>
      </div>
      <div className="p-4">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block">
          <defs>
            <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#C75D33" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#C75D33" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={areaPath} fill="url(#trendFill)" />
          <path d={linePath} fill="none" stroke="#C75D33" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
          {pts.filter((p) => p.count > 0).map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y} r="2.6" fill="#C75D33" />
          ))}
        </svg>
        <div className="flex justify-between text-[10px] text-slate-400 mt-1 px-1 tabular-nums">
          <span>{startLabel}</span>
          <span>{endLabel}</span>
        </div>
      </div>
    </div>
  );
}

function SubscriptionPanel({ data, range, setRange, setPreset }) {
  const nav = useNavigate();
  const fmt$ = (v) => "$" + Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-10" data-testid="subscription-panel">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-lg font-bold text-slate-900">Subscription & revenue</h2>
          <div className="text-xs text-slate-500 mt-0.5">Platform-wide subscription health and estimated revenue</div>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <Button
            data-testid="manage-subs-btn"
            size="sm"
            onClick={() => nav("/admin/subscriptions")}
            className="rounded-xl bg-slate-900 hover:bg-black text-white h-9"
          >
            <CreditCard className="w-4 h-4 mr-1.5" /> Manage subscriptions
          </Button>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">From</div>
            <Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="h-9 rounded-lg w-36" data-testid="sub-from" />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">To</div>
            <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="h-9 rounded-lg w-36" data-testid="sub-to" />
          </div>
          <div className="flex gap-1.5">
            <PresetBtn onClick={() => setPreset("month")} testid="sub-preset-month">This month</PresetBtn>
            <PresetBtn onClick={() => setPreset("30d")} testid="sub-preset-30d">Last 30d</PresetBtn>
            <PresetBtn onClick={() => setPreset("ytd")} testid="sub-preset-ytd">YTD</PresetBtn>
            <PresetBtn onClick={() => setPreset("all")} testid="sub-preset-all">All</PresetBtn>
          </div>
        </div>
      </div>
      {!data ? (
        <div className="px-6 py-10 text-center text-slate-500 text-sm"><Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading…</div>
      ) : (
        <div className="p-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <MetricCard label="In trial" value={data.current.trialing} accent="text-amber-600" testid="sub-trialing" />
            <MetricCard label="Active" value={data.current.active} accent="text-emerald-600" testid="sub-active" />
            <MetricCard label="Cancelled" value={data.current.cancelled} accent="text-rose-600" testid="sub-cancelled" />
            <MetricCard label="Inactive" value={data.current.inactive} accent="text-slate-500" testid="sub-inactive" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <RevenueCard icon={DollarSign} label="Total billed (range)" value={fmt$(data.revenue.total_billed)} testid="sub-total-billed" accent="text-slate-900" />
            <RevenueCard icon={Repeat} label="MRR" value={fmt$(data.revenue.mrr)} accent="text-[#C75D33]" testid="sub-mrr" />
            <RevenueCard icon={TrendingUp} label="ARR" value={fmt$(data.revenue.arr)} testid="sub-arr" />
            <RevenueCard icon={AlertTriangle} label="Churn rate" value={data.rates.churn_pct + "%"} accent={data.rates.churn_pct > 5 ? "text-rose-600" : "text-emerald-600"} testid="sub-churn" />
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <SubMini label="New signups" value={data.in_range.new_signups} />
            <SubMini label="New trials" value={data.in_range.new_trials} />
            <SubMini label="Converted to paid" value={data.in_range.new_actives} />
            <SubMini label="Cancellations" value={data.in_range.new_cancellations} />
          </div>
          <div className="text-[11px] text-slate-400 mt-3 leading-relaxed">
            Revenue estimated from configured plan rate (${data.revenue.plan_monthly}/mo, ${data.revenue.plan_trial} trial). Stripe webhooks will replace this with actual paid invoices once enabled.
          </div>
        </div>
      )}
    </div>
  );
}

function RevenueCard({ icon: Icon, label, value, accent = "text-slate-900", testid }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
        <Icon className="w-3.5 h-3.5 text-slate-400" />
      </div>
      <div className={`font-display text-2xl font-bold ${accent}`}>{value}</div>
    </div>
  );
}

function SubMini({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-display font-bold text-slate-900 text-lg">{value}</div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent = "text-slate-900", testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{label}</div>
        <Icon className="w-4 h-4 text-slate-400" />
      </div>
      <div className={`font-display text-3xl font-bold ${accent}`}>{value}</div>
    </div>
  );
}

/* ----- Per-company stats modal (aggregate only — no PII) ----- */

const STATUS_META = {
  NEW:       { label: "New",       dot: "bg-blue-500" },
  CONTACTED: { label: "Contacted", dot: "bg-amber-500" },
  SCHEDULED: { label: "Scheduled", dot: "bg-violet-500" },
  WON:       { label: "Won",       dot: "bg-emerald-500" },
  LOST:      { label: "Lost",      dot: "bg-slate-400" },
};

function CompanyStatsModal({ company, onClose }) {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const iso = (d) => d.toISOString().slice(0, 10);

  const [fromDate, setFromDate] = useState(iso(firstOfMonth));
  const [toDate, setToDate] = useState(iso(today));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/companies/${company.id}/stats`, {
        params: { from_date: fromDate, to_date: toDate },
      });
      setData(res.data);
    } catch (e) {
      toast.error("Couldn't load stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [fromDate, toDate]);

  const setPreset = (preset) => {
    const now = new Date();
    if (preset === "month") {
      setFromDate(iso(new Date(now.getFullYear(), now.getMonth(), 1)));
      setToDate(iso(now));
    } else if (preset === "30d") {
      const d = new Date(now); d.setDate(d.getDate() - 30);
      setFromDate(iso(d)); setToDate(iso(now));
    } else if (preset === "ytd") {
      setFromDate(iso(new Date(now.getFullYear(), 0, 1)));
      setToDate(iso(now));
    } else if (preset === "all") {
      setFromDate("2020-01-01");
      setToDate(iso(now));
    }
  };

  const fmt$ = (v) => "$" + Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="admin-stats-modal-backdrop">
      <div className="bg-white rounded-3xl max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="admin-stats-modal">
        <div className="px-6 sm:px-8 py-5 border-b border-slate-100 flex items-start justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-[#C75D33]">Activity</div>
            <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900 mt-1">{company.business_name}</h2>
            <div className="text-xs text-slate-500 mt-0.5 font-mono">/{company.slug}/quote</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="admin-stats-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 sm:px-8 py-5 space-y-5">
          {/* Privacy note */}
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 flex items-start gap-2.5">
            <Lock className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
            <div className="text-xs text-slate-600 leading-relaxed">
              Aggregate-only view. Individual customer details (name, contact info, addresses) are not shown to platform admins.
            </div>
          </div>

          {/* Date range */}
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">From</div>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-10 rounded-xl" data-testid="admin-stats-from" />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">To</div>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-10 rounded-xl" data-testid="admin-stats-to" />
            </div>
            <div className="flex flex-wrap gap-1.5 ml-auto">
              <PresetBtn onClick={() => setPreset("month")} testid="preset-month">This month</PresetBtn>
              <PresetBtn onClick={() => setPreset("30d")} testid="preset-30d">Last 30d</PresetBtn>
              <PresetBtn onClick={() => setPreset("ytd")} testid="preset-ytd">YTD</PresetBtn>
              <PresetBtn onClick={() => setPreset("all")} testid="preset-all">All time</PresetBtn>
            </div>
          </div>

          {loading || !data ? (
            <div className="py-12 flex items-center justify-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
          ) : (
            <>
              {/* Top metrics */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <MetricCard label="Quotes" value={data.totals.total_quotes} testid="metric-quotes" />
                <MetricCard label="Total quote $" value={fmt$(data.totals.total_quote_value)} testid="metric-total-value" />
                <MetricCard label="Avg quote $" value={fmt$(data.totals.avg_quote_value)} testid="metric-avg-value" />
                <MetricCard label="Won $" value={fmt$(data.totals.won_value)} accent="text-emerald-600" testid="metric-won-value" />
              </div>

              {/* Conversion rate strip */}
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-center justify-between gap-3" data-testid="conversion-card">
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-700">Quote → Won conversion</div>
                  <div className="font-display font-bold text-2xl text-emerald-900 mt-0.5">{data.totals.conversion_rate}%</div>
                </div>
                <div className="text-right text-xs text-emerald-800">
                  <div><strong>{data.totals.won_count}</strong> won</div>
                  <div>of {data.totals.total_quotes} quotes</div>
                </div>
              </div>

              {/* Trend chart */}
              {data.trend && data.trend.length > 1 && (
                <TrendChart trend={data.trend} />
              )}

              {/* Status breakdown */}
              <div className="rounded-2xl border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-600">Status breakdown</div>
                </div>
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left px-4 py-2 font-bold">Status</th>
                      <th className="text-right px-4 py-2 font-bold">Count</th>
                      <th className="text-right px-4 py-2 font-bold">% of quotes</th>
                      <th className="text-right px-4 py-2 font-bold">Quote $</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.breakdown.map((row) => {
                      const meta = STATUS_META[row.status] || { label: row.status, dot: "bg-slate-400" };
                      return (
                        <tr key={row.status} data-testid={`status-row-${row.status}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
                              <span className="font-semibold text-slate-900">{meta.label}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums">{row.count}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-600">{row.percent.toFixed(1)}%</td>
                          <td className="px-4 py-3 text-right tabular-nums">{fmt$(row.total_value)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="text-xs text-slate-500 leading-relaxed">
                Showing {data.range.from} → {data.range.to}. Lifetime total quotes for this company: <strong className="text-slate-700">{data.totals.lifetime_total_quotes}</strong>.
              </div>

              {/* Activity log — config changes for this company */}
              <div className="pt-2">
                <AuditLog mode="admin" companyId={company.id} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, accent = "text-slate-900", testid }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4" data-testid={testid}>
      <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className={`font-display text-2xl font-bold ${accent}`}>{value}</div>
    </div>
  );
}

function DeleteCompanyModal({ company, onClose, onDeleted }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.post(`/admin/companies/${company.id}/preview-delete`)
      .then((r) => { if (!cancelled) { setPreview(r.data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setPreview(null); setLoading(false); } });
    return () => { cancelled = true; };
  }, [company.id]);

  const slug = (company.slug || "").trim().toLowerCase();
  const canDelete = confirmText.trim().toLowerCase() === slug && slug.length > 0;

  const handleDelete = async () => {
    if (!canDelete) return;
    setBusy(true);
    try {
      const r = await api.delete(`/admin/companies/${company.id}`, {
        data: { confirm_slug: confirmText, reason },
      });
      const totalDeleted = Object.values(r.data?.summary?.deleted || {}).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Deleted ${company.business_name} (${totalDeleted} records)`);
      onDeleted && onDeleted();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
      setBusy(false);
    }
  };

  const counts = preview?.will_delete || {};
  const total = Object.entries(counts).reduce((a, [, v]) => a + (v || 0), 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="delete-company-modal">
        <div className="p-6 border-b border-rose-100 bg-rose-50/40 flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-display text-xl font-bold text-slate-900">Delete this company?</h2>
              <p className="text-sm text-slate-600 mt-0.5">This action is permanent and removes operational data.</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="delete-company-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Company</div>
            <div className="font-semibold text-slate-900">{company.business_name}</div>
            <div className="text-sm text-slate-600">{company.owner_email} · /{slug}</div>
          </div>

          {loading ? (
            <div className="text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Calculating impact…</div>
          ) : (
            <>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-rose-700 mb-2">Will be permanently deleted ({total} records)</div>
                <ul className="text-sm space-y-1" data-testid="delete-preview-list">
                  {Object.entries(counts).filter(([k]) => !k.startsWith("payment_") && !k.includes("preserved") && !k.startsWith("unassigned_leads_archived") && !k.startsWith("marketplace_disputes")).map(([k, v]) => (
                    <li key={k} className="flex justify-between border-b border-slate-100 py-1">
                      <span className="text-slate-700 capitalize">{k.replace(/_/g, " ")}</span>
                      <span className="tabular-nums font-mono text-rose-700">{v}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {(preview?.will_delete?.unassigned_leads_archived > 0 || preview?.will_delete?.payment_transactions_preserved > 0 || preview?.will_delete?.marketplace_disputes > 0) && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-xs text-amber-900">
                  <div className="font-semibold mb-1">⚠ Preserved (for compliance + accounting)</div>
                  <ul className="space-y-0.5">
                    {preview?.will_delete?.unassigned_leads_archived > 0 && (
                      <li>
                        <strong>Leads routed to this company will be ARCHIVED</strong> ({preview.will_delete.unassigned_leads_archived}) — NOT re-routed. Older leads carry stale TCPA consent so we keep them for the audit trail but never re-deliver.
                      </li>
                    )}
                    {preview?.will_delete?.payment_transactions_preserved > 0 && <li>Payment transactions: {preview.will_delete.payment_transactions_preserved} (Stripe receipts)</li>}
                    {preview?.will_delete?.marketplace_disputes > 0 && <li>Marketplace disputes: {preview.will_delete.marketplace_disputes}</li>}
                  </ul>
                </div>
              )}
            </>
          )}

          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-slate-600 mb-1.5">
              Type <code className="bg-slate-100 px-1.5 py-0.5 rounded text-rose-700">{slug}</code> to confirm
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={slug}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-rose-500 focus:ring-2 focus:ring-rose-100 outline-none font-mono"
              data-testid="delete-company-confirm-input"
              autoComplete="off"
            />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-slate-600 mb-1.5">Reason (optional)</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. test account cleanup before launch"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-slate-500 outline-none"
              data-testid="delete-company-reason-input"
              maxLength={300}
            />
          </div>
        </div>

        <div className="p-6 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="delete-company-cancel">Cancel</Button>
          <Button
            onClick={handleDelete}
            disabled={!canDelete || busy}
            className="bg-rose-600 hover:bg-rose-700 text-white font-semibold disabled:opacity-50"
            data-testid="delete-company-confirm-btn"
          >
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting…</> : <><Trash2 className="w-4 h-4 mr-2" /> Permanently delete</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PresetBtn({ onClick, children, testid }) {
  return (
    <button onClick={onClick} className="text-xs font-semibold text-slate-600 hover:text-slate-900 px-2.5 py-1 rounded-lg border border-slate-200 hover:border-slate-300 bg-white" data-testid={testid}>
      {children}
    </button>
  );
}


function DisputesPanel() {
  const [disputes, setDisputes] = useState([]);
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/marketplace/disputes");
      setDisputes(data.disputes || []);
    } catch {
      toast.error("Couldn't load disputes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resolve = async (d, action) => {
    setBusy(d.id);
    try {
      await api.post(`/admin/marketplace/disputes/${d.id}/resolve`, { action });
      toast.success(action === "credit" ? "Credited" : "Denied");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't resolve");
    } finally {
      setBusy(null);
    }
  };

  const open = disputes.filter((d) => d.status === "open");
  const resolved = disputes.filter((d) => d.status !== "open");

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-8" data-testid="disputes-panel">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-bold text-slate-900">Marketplace credit requests</h2>
          <p className="text-xs text-slate-500 mt-0.5">Companies disputing a delivered lead. Credit returns funds to their wallet.</p>
        </div>
        {open.length > 0 && (
          <span className="text-[10px] font-extrabold uppercase tracking-widest px-2 py-1 rounded-full bg-amber-100 text-amber-800" data-testid="disputes-open-count">
            {open.length} open
          </span>
        )}
      </div>
      <div className="p-5 sm:p-6">
        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : disputes.length === 0 ? (
          <div className="text-sm text-slate-500">No credit requests yet.</div>
        ) : (
          <div className="space-y-3">
            {open.map((d) => (
              <div key={d.id} className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900">{d.company_name || d.company_slug || "Unknown company"}</div>
                    <div className="text-xs text-slate-600 mt-1">
                      Reason: <strong>{d.reason}</strong> · Charge: <strong>${(d.charge_cents / 100).toFixed(2)}</strong> · Filed {new Date(d.created_at).toLocaleString()}
                    </div>
                    {d.detail && <div className="text-xs text-slate-700 mt-2 italic">"{d.detail}"</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      className="rounded-lg bg-emerald-600 hover:bg-emerald-700 h-9"
                      disabled={busy === d.id}
                      onClick={() => resolve(d, "credit")}
                      data-testid={`dispute-credit-${d.id}`}
                    >
                      {busy === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <>Credit ${(d.charge_cents / 100).toFixed(0)}</>}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-lg border-slate-300 h-9"
                      disabled={busy === d.id}
                      onClick={() => resolve(d, "deny")}
                      data-testid={`dispute-deny-${d.id}`}
                    >
                      Deny
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            {resolved.length > 0 && (
              <div className="mt-2">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 mt-4">Resolved ({resolved.length})</div>
                <div className="space-y-1.5">
                  {resolved.slice(0, 10).map((d) => (
                    <div key={d.id} className="flex items-center justify-between text-xs px-3 py-2 rounded-lg border border-slate-100 bg-slate-50">
                      <span className="text-slate-700 truncate pr-3">{d.company_name} · {d.reason}</span>
                      <span className={`font-bold shrink-0 ${d.status === "credited" ? "text-emerald-600" : "text-slate-500"}`}>
                        {d.status === "credited" ? `Credited $${(d.charge_cents / 100).toFixed(0)}` : "Denied"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function LeadSourcesPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [sendingDigest, setSendingDigest] = useState(false);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/lead-sources?days=${d}`);
      setData(data);
    } catch {
      toast.error("Couldn't load lead sources");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); /* eslint-disable-next-line */ }, [days]);

  const fireDigests = async () => {
    if (!window.confirm("Send today's marketplace digest to all opted-in companies?")) return;
    setSendingDigest(true);
    try {
      const { data } = await api.post("/admin/marketplace/send-digests");
      const sent = (data.digests || []).filter(d => d.sent).length;
      toast.success(`Digest run: ${sent} email${sent === 1 ? "" : "s"} sent (${data.ran} companies checked)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Digest run failed");
    } finally {
      setSendingDigest(false);
    }
  };

  const topZip = (data?.by_zip || [])[0];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-8" data-testid="lead-sources-panel">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><Target className="w-4 h-4 text-[#C75D33]" /> Lead source analytics</h2>
          <p className="text-xs text-slate-500 mt-0.5">Where homeowner demand is coming from. Aim paid acquisition at the densest unfilled zips.</p>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`lead-sources-range-${d}`}
              className={`text-xs font-bold px-2.5 py-1.5 rounded-lg ${
                days === d ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {d}d
            </button>
          ))}
          <button
            onClick={fireDigests}
            disabled={sendingDigest}
            className="text-xs font-bold px-3 py-1.5 rounded-lg bg-[#C75D33] hover:bg-[#A84C28] text-white inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid="lead-sources-send-digest"
          >
            {sendingDigest ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Send digests
          </button>
        </div>
      </div>
      <div className="p-5 sm:p-6">
        {loading || !data ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : data.summary.total_leads === 0 ? (
          <div className="text-sm text-slate-500">No leads yet in this window.</div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
              <SumCard label="Total leads" value={data.summary.total_leads} accent="text-slate-900" />
              <SumCard label="Assigned" value={data.summary.total_assigned} accent="text-emerald-600" sub={`${data.summary.total_leads ? Math.round(100 * data.summary.total_assigned / data.summary.total_leads) : 0}% fill rate`} />
              <SumCard label="Platform revenue" value={`$${(data.summary.platform_revenue_cents / 100).toFixed(2)}`} accent="text-[#C75D33]" />
              <SumCard label="Top zip" value={topZip ? topZip.zip : "—"} accent="text-slate-900" sub={topZip ? `${topZip.leads} leads · ${topZip.state}` : ""} />
            </div>
            <div className="grid lg:grid-cols-3 gap-5">
              {/* Top ZIPs */}
              <div className="lg:col-span-2 rounded-2xl border border-slate-200 overflow-hidden">
                <div className="px-4 py-2.5 border-b border-slate-100 text-[10px] font-bold uppercase tracking-widest text-slate-500 bg-slate-50 flex items-center gap-1.5">
                  <MapPin className="w-3 h-3" /> Top demand zips
                </div>
                <div className="max-h-80 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-widest text-slate-500">
                        <th className="text-left px-4 py-2 font-bold">ZIP</th>
                        <th className="text-left px-2 py-2 font-bold">ST</th>
                        <th className="text-right px-2 py-2 font-bold">Leads</th>
                        <th className="text-right px-2 py-2 font-bold">Unfilled</th>
                        <th className="text-right px-4 py-2 font-bold">Fill</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.by_zip.slice(0, 30).map((z) => (
                        <tr key={z.zip} className="border-t border-slate-100">
                          <td className="px-4 py-2 font-mono text-slate-900">{z.zip}</td>
                          <td className="px-2 py-2 text-slate-500">{z.state || "—"}</td>
                          <td className="px-2 py-2 text-right font-bold text-slate-900">{z.leads}</td>
                          <td className="px-2 py-2 text-right text-amber-600 font-semibold">{z.unassigned}</td>
                          <td className={`px-4 py-2 text-right font-bold ${z.fill_rate >= 0.7 ? "text-emerald-600" : z.fill_rate >= 0.3 ? "text-amber-600" : "text-rose-600"}`}>
                            {Math.round(z.fill_rate * 100)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Right column: by state + by service */}
              <div className="space-y-4">
                <div className="rounded-2xl border border-slate-200 overflow-hidden">
                  <div className="px-4 py-2.5 border-b border-slate-100 text-[10px] font-bold uppercase tracking-widest text-slate-500 bg-slate-50">By state</div>
                  <ul className="divide-y divide-slate-100">
                    {data.by_state.slice(0, 8).map((s) => (
                      <li key={s.state} className="flex items-center justify-between px-4 py-2 text-sm">
                        <span className="font-semibold text-slate-900">{s.state}</span>
                        <span className="text-slate-600">{s.leads} <span className="text-slate-400 text-xs">({s.assigned} ass.)</span></span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-2xl border border-slate-200 overflow-hidden">
                  <div className="px-4 py-2.5 border-b border-slate-100 text-[10px] font-bold uppercase tracking-widest text-slate-500 bg-slate-50">By service</div>
                  <ul className="divide-y divide-slate-100">
                    {data.by_service.slice(0, 6).map((s) => (
                      <li key={s.service_key} className="flex items-center justify-between px-4 py-2 text-sm">
                        <span className="font-semibold text-slate-900 capitalize">{s.service_key.replace(/_/g, " ")}</span>
                        <span className="text-slate-600">{s.leads}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SumCard({ label, value, sub, accent }) {
  return (
    <div className="rounded-2xl border border-slate-200 p-4 bg-slate-50/50">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-display text-2xl font-extrabold mt-1 ${accent || "text-slate-900"}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

